import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { auditKeys, complianceUserKeys } from "@/lib/query";

export interface AuditListItem {
  id: string;
  templateId: string;
  templateName: string;
  projectId: string | null;
  projectName: string;
  accountName: string | null;
  assignedToId: string;
  assignedToName: string | null;
  createdById: string;
  createdByName: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  totalItems: number;
  checkedItems: number;
}

export interface AuditItemNoteRow {
  id: string;
  auditItemId: string;
  authorId: string;
  authorName: string | null;
  body: string;
  createdAt: string;
}

export interface AuditItemRow {
  id: string;
  auditId: string;
  templateItemId: string | null;
  sectionTitle: string;
  sectionOrder: number;
  itemText: string;
  itemOptional: boolean;
  itemUpload: boolean;
  itemOrder: number;
  checked: boolean;
  latestNote: AuditItemNoteRow | null;
  noteCount: number;
  updatedAt: string;
}

export interface AuditSection {
  sectionTitle: string;
  sectionOrder: number;
  items: AuditItemRow[];
}

export interface AuditNoteRow {
  id: string;
  auditId: string;
  authorId: string;
  authorName: string | null;
  body: string;
  context: "general" | "blocked" | "resolution";
  createdAt: string;
}

export interface AuditDetail extends AuditListItem {
  sections: AuditSection[];
  items: AuditItemRow[];
  notes: AuditNoteRow[];
  findings: string | null;
  assignedTo: { id: string; name: string; email: string } | null;
  createdBy: { id: string; name: string; email: string } | null;
  completedBy: { id: string; name: string } | null;
}

export interface ComplianceUser {
  id: string;
  name: string;
  email: string;
  role: string;
  roles: string[];
}

export function useAudits(filters?: { status?: string }) {
  return useQuery({
    queryKey: auditKeys.list(filters),
    queryFn: async (): Promise<AuditListItem[]> => {
      const params = new URLSearchParams();
      if (filters?.status) params.set("status", filters.status);
      const res = await fetch(`/api/audits?${params}`);
      if (!res.ok) throw new Error("Failed to fetch audits");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useAudit(id: string | undefined) {
  return useQuery({
    queryKey: id ? auditKeys.detail(id) : (["audits", "detail", null] as const),
    enabled: !!id,
    queryFn: async (): Promise<AuditDetail> => {
      const res = await fetch(`/api/audits/${id}`);
      if (!res.ok) throw new Error("Failed to fetch audit");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useCreateAudit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      templateId: string;
      projectId?: string;
      projectName: string;
      assignedToId: string;
    }) => {
      const res = await fetch("/api/audits", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create audit");
      const json = await res.json();
      return json.data as AuditListItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.all });
      toast.success("Audit created");
    },
    onError: () => {
      toast.error("Failed to create audit");
    },
  });
}

export function useRequestGuidance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      templateId: string;
      projectId?: string;
      projectName: string;
      assignedToId: string;
      message: string;
    }) => {
      const res = await fetch("/api/audits/guidance-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Failed to send guidance request");
      return json.data as AuditListItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.all });
      toast.success("Guidance request sent");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to send guidance request");
    },
  });
}

export function useRequestGuidanceForAudit(auditId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (message: string) => {
      const res = await fetch(`/api/audits/${auditId}/guidance-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Failed to send guidance request");
      return json.data as AuditListItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.all });
      queryClient.invalidateQueries({ queryKey: auditKeys.detail(auditId) });
      toast.success("Guidance request sent");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to send guidance request");
    },
  });
}

export function useUpdateAuditItems(auditId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (items: Array<{ id: string; checked: boolean }>) => {
      const res = await fetch(`/api/audits/${auditId}/items`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error("Failed to update audit items");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.detail(auditId) });
    },
    onError: () => {
      toast.error("Failed to save changes");
    },
  });
}

export function useAuditItemNotes(auditId: string, itemId: string | null) {
  return useQuery({
    queryKey: itemId ? auditKeys.itemNotes(auditId, itemId) : (["audit-item-notes", null] as const),
    enabled: !!itemId,
    queryFn: async (): Promise<AuditItemNoteRow[]> => {
      const res = await fetch(`/api/audits/${auditId}/items/${itemId}/notes`);
      if (!res.ok) throw new Error("Failed to fetch item notes");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useCreateAuditItemNote(auditId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: string) => {
      const res = await fetch(`/api/audits/${auditId}/items/${itemId}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body }),
      });
      if (!res.ok) throw new Error("Failed to add note");
      const json = await res.json();
      return json.data as AuditItemNoteRow;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.itemNotes(auditId, itemId) });
      queryClient.invalidateQueries({ queryKey: auditKeys.detail(auditId) });
    },
    onError: () => {
      toast.error("Failed to add note");
    },
  });
}

const STATUS_LABELS: Record<string, string> = {
  assigned: "Assigned",
  guidance_requested: "Guidance Requested",
  in_progress: "In Progress",
  complete: "Complete",
  blocked: "Blocked",
};

export function useSetAuditStatus(auditId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      status: string;
      blockedReason?: string;
      resolutionNote?: string;
      recordedInPmApp?: boolean;
      findings?: string;
    }) => {
      const res = await fetch(`/api/audits/${auditId}/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Failed to update status");
      return json.data;
    },
    onSuccess: (_, input) => {
      queryClient.invalidateQueries({ queryKey: auditKeys.all });
      queryClient.invalidateQueries({ queryKey: auditKeys.detail(auditId) });
      toast.success(`Status set to ${STATUS_LABELS[input.status] ?? input.status}`);
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to update status");
    },
  });
}

export function useCreateAuditNote(auditId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: string) => {
      const res = await fetch(`/api/audits/${auditId}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body }),
      });
      if (!res.ok) throw new Error("Failed to add note");
      const json = await res.json();
      return json.data as AuditNoteRow;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.detail(auditId) });
    },
    onError: () => {
      toast.error("Failed to add note");
    },
  });
}

export function useUpdateAuditNote(auditId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ noteId, body }: { noteId: string; body: string }) => {
      const res = await fetch(`/api/audits/${auditId}/notes/${noteId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Failed to update note");
      return json.data as AuditNoteRow;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.detail(auditId) });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to update note");
    },
  });
}

export function useDeleteAuditNote(auditId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (noteId: string) => {
      const res = await fetch(`/api/audits/${auditId}/notes/${noteId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete note");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.detail(auditId) });
    },
    onError: () => {
      toast.error("Failed to delete note");
    },
  });
}

export function useDeleteAudit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (auditId: string) => {
      const res = await fetch(`/api/audits/${auditId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete audit");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.all });
      toast.success("Audit deleted");
    },
    onError: () => {
      toast.error("Failed to delete audit");
    },
  });
}

export function useComplianceUsers() {
  return useQuery({
    queryKey: complianceUserKeys.all,
    queryFn: async (): Promise<ComplianceUser[]> => {
      const res = await fetch("/api/compliance/users");
      if (!res.ok) throw new Error("Failed to fetch compliance users");
      const json = await res.json();
      return json.data;
    },
  });
}
